'use client';

import { Moon, Sun } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTheme } from 'next-themes';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useLocationPreference } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-location-preference';
import { useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import { NotificationsPopover } from '@/components/notifications/notifications-popover';
import { PanRallado } from '@/components/pan-rallado';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/provider/auth-provider';

export function TotemHeader() {
  const { totem, orderType } = useTotem();
  const { user } = useAuth();
  const location = totem?.locations;
  const pathname = usePathname();
  const isDashboard = pathname?.includes('/takeorder');
  const { data: locationPreference } = useLocationPreference(totem?.location_id ?? undefined);

  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!location) return null;

  if (user && isDashboard) {
    const hasOrderOptions = locationPreference?.take_away || locationPreference?.eat_here;
    // Only block if the overlay is expected (at least one option enabled) but not yet selected
    if (hasOrderOptions && !orderType) return null;

    return (
      <header className="bg-background/50 sticky top-0 z-10 w-full backdrop-blur-xs border-b flex flex-col shrink-0">
        <div className="flex h-16 w-full shrink-0 flex-row items-center gap-2 pr-2">
          <div className="flex items-center justify-start px-4 flex-1">
            <SidebarTrigger className="-ml-1" />
            <Separator className="mr-2 h-4" orientation="vertical" />
            <PanRallado />
          </div>

          <Suspense fallback={<div className="w-10 h-10 animate-pulse rounded-full bg-muted" />}>
            <NotificationsPopover />
          </Suspense>
        </div>
      </header>
    );
  }

  const title = location.title ?? location.name;
  const logoUrl = location.logo ? `${location.logo}?updated=${location.updated}` : null;

  return (
    <header className="flex flex-col shrink-0 border-b bg-background">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="relative size-16 shrink-0 overflow-hidden rounded-lg border bg-muted">
            {logoUrl ? (
              <Image alt={`Logo de ${title}`} className="object-cover" fill sizes="64px" src={logoUrl} />
            ) : (
              <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
                {title.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <h1 className={cn('text-2xl font-bold tracking-tight text-foreground')}>{title}</h1>
        </div>

        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="p-2.5 text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50 rounded-full"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label="Alternar tema"
          >
            {mounted ? (
              resolvedTheme === 'dark' ? (
                <Sun className="size-6" />
              ) : (
                <Moon className="size-6" />
              )
            ) : (
              <div className="size-6" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
